import React, { Component } from 'react';

import memoizeOne from 'memoize-one';

import FileTreeDirectory from './file-tree-directory';
import { FileTreeFile as FileTreeFileDI } from './file-tree-file';
import { flattenDirectories, isFile } from './flatten-directories';
import * as styles from './styled';
import { DirectoryEntry, FileClickProps, FileEntry, TreeEntry } from './types';

export type FileTreeProps = {
  /** Array of file tree entries to render. */
  fileTree: TreeEntry[] | null;
  /** Whether or not the entire tree should be expanded */
  expandAll?: boolean;
  /** Click handler to be invoked on any file entry click and to be given metadata about the item clicked */
  onClick?: (event: React.MouseEvent, props: FileClickProps) => void;
  /* eslint-disable react/no-unused-prop-types */
  activeDiff?: string;
  FileTreeFile?: typeof FileTreeFileDI;
};

export class FileTree extends Component<FileTreeProps, {}> {
  flattenFileTree = memoizeOne(
    (fileTree: TreeEntry[] | null) => fileTree?.map(flattenDirectories) || []
  );

  renderTreeEntry = (treeEntry: TreeEntry) => {
    return treeEntry.type === 'directory'
      ? this.renderDirectory(treeEntry)
      : this.renderFile(treeEntry);
  };

  countFiles = (contents: TreeEntry[]): number =>
    contents.reduce(
      (totalCount, entry) =>
        totalCount + (isFile(entry) ? 1 : this.countFiles(entry.contents)),
      0
    );

  renderDirectory = (directory: DirectoryEntry) => {
    const { expandAll } = this.props;
    const { name, contents } = directory;

    const fileCount = this.countFiles(contents);

    return (
      <FileTreeDirectory
        name={name}
        fileCount={fileCount}
        defaultCollapsed={!expandAll}
        key={name}
      >
        {contents.map(this.renderTreeEntry)}
      </FileTreeDirectory>
    );
  };

  renderFile = (file: FileEntry) => {
    const { onClick, FileTreeFile = FileTreeFileDI } = this.props;
    const {
      name,
      comments,
      fileDiffStatus,
      href,
      isConflicted,
      isHidden,
      linesAdded,
      linesRemoved,
    } = file;

    const fileTreeFile = (
      <li key={name}>
        <FileTreeFile
          name={name}
          comments={comments}
          onClick={onClick}
          href={href}
          fileDiffStatus={fileDiffStatus}
          isConflicted={isConflicted}
          isHidden={isHidden}
          linesAdded={linesAdded}
          linesRemoved={linesRemoved}
        />
      </li>
    );

    return fileTreeFile;
  };

  render() {
    const flattenedFileTree = this.flattenFileTree(this.props.fileTree);
    return (
      <styles.FileTreeWrapper role="list">
        {flattenedFileTree.map(this.renderTreeEntry)}
      </styles.FileTreeWrapper>
    );
  }
}
